import { useEffect, useLayoutEffect, useState, useCallback, useRef, Suspense, lazy, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Save,
  Eye,
  Edit3,
  ZoomIn,
  ZoomOut,
  ExternalLink,
  ArrowLeft
} from 'lucide-react'
import { Button } from '../components/common'
import useSurveyStore from '../stores/surveyStore'
import useCategoryStore from '../stores/categoryStore'
import useMobileLayout from '../hooks/useMobileLayout'
import { SurveyDocument } from '../pdf/templates/SurveyDocument'
import { BlobProvider, pdf } from '@react-pdf/renderer'

// Lazy load PDFViewer to avoid issues with SSR and initial load
const PDFViewer = lazy(() =>
  import('@react-pdf/renderer').then(module => ({ default: module.PDFViewer }))
)

// Auto-resizing textarea component
function AutoResizeTextarea({ value, onChange, placeholder, minHeight = 30, style = {}, ...props }) {
  const textareaRef = useRef(null)

  const resize = useCallback(() => {
    const textarea = textareaRef.current
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = '0px'
      const newHeight = Math.max(minHeight, textarea.scrollHeight)
      textarea.style.height = newHeight + 'px'
    }
  }, [minHeight])

  // Use useLayoutEffect to resize before browser paint
  useLayoutEffect(() => {
    resize()
  }, [value, resize])

  // Also resize on mount after a small delay to ensure DOM is ready
  useEffect(() => {
    const timer = setTimeout(resize, 0)
    return () => clearTimeout(timer)
  }, [resize])

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={(e) => {
        onChange(e.target.value)
        // Immediate resize on input
        requestAnimationFrame(resize)
      }}
      placeholder={placeholder}
      rows={1}
      style={{
        ...style,
        minHeight: `${minHeight}px`,
        overflow: 'hidden',
        resize: 'none',
        display: 'block',
        width: '100%'
      }}
      {...props}
    />
  )
}

// Detect iOS devices
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

const WORKER_URL = 'https://clystcore-api.js-55b.workers.dev'

// RICS section/category definitions
const SECTIONS = {
  D: {
    name: 'Outside the property',
    categories: {
      D1: 'Chimney stacks', D2: 'Roof coverings', D3: 'Rainwater pipes and gutters',
      D4: 'Main walls', D5: 'Windows', D6: 'Outside doors', D7: 'Conservatory and porches',
      D8: 'Other joinery and finishes', D9: 'Other'
    }
  },
  E: {
    name: 'Inside the property',
    categories: {
      E1: 'Roof structure', E2: 'Ceilings', E3: 'Walls and partitions', E4: 'Floors',
      E5: 'Fireplaces, chimney breasts and flues', E6: 'Built-in fittings', E7: 'Woodwork',
      E8: 'Bathroom fittings', E9: 'Other'
    }
  },
  F: {
    name: 'Services',
    categories: {
      F1: 'Electricity', F2: 'Gas/oil', F3: 'Water', F4: 'Heating', F5: 'Water heating',
      F6: 'Drainage', F7: 'Common services'
    }
  },
  G: {
    name: 'Grounds',
    categories: {
      G1: 'Garage', G2: 'Outbuildings', G3: 'Other'
    }
  }
}

// Rating circle component (matches PDF output exactly)
function RatingCircle({ rating, size = 20, editable = false, onChange }) {
  const colors = {
    '1': { bg: '#92D050', text: '#fff', border: '#000' },
    '2': { bg: '#FFC000', text: '#fff', border: '#000' },
    '3': { bg: '#FF0000', text: '#fff', border: '#000' },
    'NI': { bg: '#fff', text: '#000', border: '#000' }
  }

  const style = colors[rating] || colors['NI']
  const displayText = rating || 'NI'

  if (editable) {
    return (
      <select
        value={rating || 'NI'}
        onChange={(e) => onChange(e.target.value)}
        className="rating-select"
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: style.bg,
          color: style.text,
          border: `1.5px solid ${style.border}`,
          fontWeight: 'bold',
          fontSize: displayText === 'NI' ? size * 0.4 : size * 0.5,
          textAlign: 'center',
          textAlignLast: 'center',
          cursor: 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          padding: 0,
          lineHeight: `${size}px`
        }}
      >
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="NI">NI</option>
      </select>
    )
  }

  return (
    <div style={{
      width: size,
      height: size,
      borderRadius: '50%',
      background: style.bg,
      border: `1.5px solid ${style.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: rating === 'NI' ? size * 0.4 : size * 0.5,
      color: style.text,
      flexShrink: 0
    }}>
      {displayText}
    </div>
  )
}

// PNG header templates for each section (matching SurveyDocument.jsx)
const SECTION_HEADERS = {
  D: '/D_header.png?v=1',
  E: '/E_header.png?v=3',
  F: '/F_header.png?v=1',
  G: '/G_header.png?v=1'
}

// Section header component
function SectionHeader({ sectionLetter, surveyLevel, limitationsText, editMode, onLimitationsChange }) {
  const hasHeaderImage = SECTION_HEADERS[sectionLetter]

  // Use PNG header template if available
  if (hasHeaderImage) {
    return (
      <div className="section-header" style={{ marginBottom: '20px', marginLeft: '-40px', marginRight: '-40px', marginTop: '-40px' }}>
        {/* PNG Header Image - full width to page edge */}
        <img
          src={hasHeaderImage}
          alt={`Section ${sectionLetter} Header`}
          style={{
            width: '595px',
            display: 'block'
          }}
        />

        {/* Limitations box only (label is in the header image) - matches category width */}
        <div style={{ marginLeft: '65px', marginRight: '40px', marginTop: '2px', marginBottom: '15px' }}>
          <div style={{
            border: '1px solid #999',
            borderRadius: '4px',
            minHeight: '20px',
            padding: editMode ? '0' : '6px',
            fontSize: '9px',
            overflow: 'hidden'
          }}>
            {editMode ? (
              <AutoResizeTextarea
                value={limitationsText || ''}
                onChange={onLimitationsChange}
                placeholder="Enter any limitations..."
                minHeight={30}
                style={{
                  width: '100%',
                  border: 'none',
                  padding: '6px',
                  fontSize: '9px',
                  fontFamily: 'inherit',
                  background: '#fffef0',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            ) : (limitationsText || '')}
          </div>
        </div>

        {/* Rating legend */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px', marginRight: '40px', alignItems: 'center' }}>
          <RatingCircle rating="1" size={20} />
          <RatingCircle rating="2" size={20} />
          <RatingCircle rating="3" size={20} />
          <RatingCircle rating="NI" size={20} />
        </div>
      </div>
    )
  }

  // Fallback: text-based header for sections without PNG templates
  return (
    <div className="section-header" style={{ marginBottom: '20px' }}>
      {/* Top bar with RICS branding */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #5B2D8C 0%, #8B4CA8 100%)',
          color: 'white',
          padding: '4px 16px',
          fontSize: '10px'
        }}>
          RICS Home Survey – Level {surveyLevel}
        </div>
      </div>

      {/* Main header row */}
      <div style={{ display: 'flex', gap: 0, marginBottom: '15px' }}>
        {/* Purple section letter box */}
        <div style={{
          width: '62px',
          minHeight: '70px',
          background: 'linear-gradient(180deg, #8B6AAF 0%, #5B2D8C 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <span style={{ color: 'white', fontSize: '48px', fontWeight: 'bold' }}>{sectionLetter}</span>
        </div>

        {/* Title area */}
        <div style={{ flex: 1, paddingLeft: '20px', display: 'flex', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', color: '#000' }}>
            Full detail of elements inspected
          </h2>
        </div>

        {/* RICS text */}
        <div style={{ display: 'flex', alignItems: 'flex-start', paddingTop: '5px' }}>
          <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#000', letterSpacing: '2px' }}>RICS</span>
        </div>
      </div>

      {/* Limitations section */}
      <div style={{ marginLeft: '82px', marginBottom: '10px' }}>
        <div style={{ fontSize: '11px', fontWeight: 'bold', marginBottom: '5px' }}>Limitations on the inspection</div>
        <div style={{
          border: '1px solid #000',
          minHeight: '22px',
          marginBottom: '15px',
          padding: editMode ? '0' : '4px 8px',
          fontSize: '10px'
        }}>
          {editMode ? (
            <AutoResizeTextarea
              value={limitationsText || ''}
              onChange={onLimitationsChange}
              placeholder="Enter any limitations..."
              minHeight={40}
              style={{
                width: '100%',
                border: 'none',
                padding: '4px 8px',
                fontSize: '10px',
                fontFamily: 'inherit',
                background: '#fffef0',
                boxSizing: 'border-box',
                outline: 'none'
              }}
            />
          ) : (limitationsText || '')}
        </div>
      </div>

      {/* Rating legend */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px', alignItems: 'center' }}>
        <RatingCircle rating="1" size={20} />
        <RatingCircle rating="2" size={20} />
        <RatingCircle rating="3" size={20} />
        <RatingCircle rating="NI" size={20} />
      </div>
    </div>
  )
}

// Category component
function CategoryBlock({ code, name, comment, rating, photos, editMode, onCommentChange, onRatingChange, usesPngHeader }) {
  const hasPhotos = photos && photos.length > 0
  const hasContent = hasPhotos || comment

  // Margin matches PDF: 65px normally, but 25px (65-40) for PNG header sections
  const categoryMargin = usesPngHeader ? '25px' : '65px'

  // Photo rows (2 per row) - matching PDF dimensions exactly
  const photoRows = []
  for (let i = 0; i < photos.length; i += 2) {
    const photo1 = photos[i]
    const photo2 = photos[i + 1]
    photoRows.push(
      <div key={i} style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <img
            src={`${WORKER_URL}/api/photos/${photo1.id}/image`}
            alt={`${code}-${i + 1}`}
            style={{ maxWidth: '200px', maxHeight: '160px', objectFit: 'contain', borderRadius: '8px' }}
          />
          <div style={{ fontSize: '8px', marginTop: '3px', color: '#666' }}>{code}-{i + 1}</div>
        </div>
        {photo2 ? (
          <div style={{ flex: 1, textAlign: 'center' }}>
            <img
              src={`${WORKER_URL}/api/photos/${photo2.id}/image`}
              alt={`${code}-${i + 2}`}
              style={{ maxWidth: '200px', maxHeight: '160px', objectFit: 'contain', borderRadius: '8px' }}
            />
            <div style={{ fontSize: '8px', marginTop: '3px', color: '#666' }}>{code}-{i + 2}</div>
          </div>
        ) : <div style={{ flex: 1 }} />}
      </div>
    )
  }

  // Empty category - no content, no photos (preview mode only)
  if (!hasContent && !editMode) {
    return (
      <div className="category" style={{ marginBottom: '12px', marginLeft: categoryMargin }}>
        <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>{code} {name}</div>
        <div style={{
          border: '1px solid #999',
          borderRadius: '4px',
          padding: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          minHeight: '35px'
        }}>
          <RatingCircle rating={rating || 'NI'} size={20} />
        </div>
      </div>
    )
  }

  return (
    <div className="category" style={{ marginBottom: '12px', marginLeft: categoryMargin }}>
      <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '4px' }}>{code} {name}</div>
      <div style={{ border: '1px solid #999', borderRadius: '4px', overflow: 'hidden' }}>
        {/* Description row */}
        <div style={{ padding: '8px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, fontSize: '9px', lineHeight: 1.4 }}>
            {editMode ? (
              <AutoResizeTextarea
                value={comment || ''}
                onChange={onCommentChange}
                placeholder="Add description..."
                minHeight={hasPhotos ? 60 : 30}
                style={{
                  width: '100%',
                  border: '1px dashed #ccc',
                  borderRadius: '4px',
                  padding: '8px',
                  fontSize: '9px',
                  fontFamily: 'inherit',
                  background: '#fffef0',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
            ) : <span style={{ whiteSpace: 'pre-wrap' }}>{comment || ''}</span>}
          </div>
          <div style={{ flexShrink: 0, marginLeft: '10px' }}>
            {editMode ? (
              <RatingCircle
                rating={rating || 'NI'}
                size={20}
                editable
                onChange={onRatingChange}
              />
            ) : <RatingCircle rating={rating || 'NI'} size={20} />}
          </div>
        </div>
        {/* Photos section - matching PDF exactly */}
        {hasPhotos && (
          <div style={{ padding: '8px', borderTop: '1px solid #E0E0E0' }}>
            {photoRows}
          </div>
        )}
      </div>
    </div>
  )
}

// A4 page dimensions - matching @react-pdf/renderer exactly
const A4_WIDTH_PX = 595 // A4 width in points (same as @react-pdf)
const A4_HEIGHT_PX = 842 // A4 height in points
const PAGE_PADDING_TOP = 40
const PAGE_PADDING_BOTTOM = 50
const PAGE_PADDING_LEFT = 40
const PAGE_PADDING_RIGHT = 40
const CONTENT_HEIGHT = A4_HEIGHT_PX - PAGE_PADDING_TOP - PAGE_PADDING_BOTTOM // ~752px available

// Height estimates (matching @react-pdf layout)
const SECTION_HEADER_HEIGHT = 110 // PNG header is ~110px
const LIMITATIONS_BOX_HEIGHT = 50
const EMPTY_CATEGORY_HEIGHT = 55
const CATEGORY_BASE_HEIGHT = 60
const PHOTO_ROW_HEIGHT = 180
const TEXT_LINE_HEIGHT = 14

// Estimate height of a category block
function estimateCategoryHeight(comment, photoCount) {
  let height = CATEGORY_BASE_HEIGHT

  // Add height for text (rough estimate: ~50 chars per line)
  if (comment) {
    const lines = Math.ceil(comment.length / 80) + 1
    height += lines * TEXT_LINE_HEIGHT
  }

  // Add height for photos (2 per row)
  if (photoCount > 0) {
    const photoRows = Math.ceil(photoCount / 2)
    height += photoRows * PHOTO_ROW_HEIGHT
  }

  return height
}

// Single A4 Page component
function A4Page({ children, pageNumber, totalPages, surveyLevel }) {
  return (
    <div
      className="a4-page"
      style={{
        width: `${A4_WIDTH_PX}px`,
        minHeight: `${A4_HEIGHT_PX}px`,
        padding: `${PAGE_PADDING_TOP}px ${PAGE_PADDING_RIGHT}px ${PAGE_PADDING_BOTTOM}px ${PAGE_PADDING_LEFT}px`,
        background: '#fff',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        boxSizing: 'border-box',
        marginBottom: '20px',
        position: 'relative',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: '10px',
        lineHeight: 1.4,
        color: '#000'
      }}
    >
      {children}
      {/* Page footer - matching PDF exactly */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: `${PAGE_PADDING_LEFT}px`,
        right: `${PAGE_PADDING_RIGHT}px`,
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '8px',
        color: '#666'
      }}>
        <span>RICS Home Survey - Level {surveyLevel}</span>
        <span>Page {pageNumber} of {totalPages}</span>
      </div>
    </div>
  )
}

// Main PDF Preview component - A4 pages with automatic pagination
function PDFPreview({ photos, categoryData, onCategoryChange, editMode, surveyLevel = 1, customCategories = [] }) {
  const getPhotosForCategory = (code) => photos.filter(p => p.categoryCode === code)

  // Get custom categories for a specific section
  const getCustomCategoriesForSection = (sectionLetter) => {
    return customCategories.filter(c => c.section === sectionLetter)
  }

  // Get standalone custom categories
  const standaloneCategories = customCategories.filter(c => !c.section)

  // Build all content items with height estimates and paginate
  const pages = []
  let currentPage = []
  let currentHeight = 0
  let pageNumber = 1

  Object.entries(SECTIONS).forEach(([sectionLetter, section]) => {
    const limitationsKey = `${sectionLetter}_limitations`
    const limitationsText = categoryData[limitationsKey]?.comment || ''
    const hasPngHeader = !!SECTION_HEADERS[sectionLetter]

    // Section header - always starts content (may start new page if not first)
    const headerHeight = SECTION_HEADER_HEIGHT + LIMITATIONS_BOX_HEIGHT

    // For sections with PNG headers, ALWAYS start a new page (like the actual PDF)
    // For other sections, start new page if header won't fit
    if (currentHeight > 0 && (hasPngHeader || currentHeight + headerHeight > CONTENT_HEIGHT)) {
      pages.push({ items: currentPage, pageNum: pageNumber++ })
      currentPage = []
      currentHeight = 0
    }

    // Add section header
    currentPage.push({
      type: 'header',
      sectionLetter,
      surveyLevel,
      limitationsText,
      limitationsKey
    })
    currentHeight += headerHeight

    // Add categories
    Object.entries(section.categories).forEach(([code, name]) => {
      const catPhotos = getPhotosForCategory(code)
      const catData = categoryData[code] || {}
      const hasContent = catData.comment || catPhotos.length > 0

      const itemHeight = hasContent
        ? estimateCategoryHeight(catData.comment, catPhotos.length)
        : EMPTY_CATEGORY_HEIGHT

      // If category won't fit, start new page
      if (currentHeight + itemHeight > CONTENT_HEIGHT) {
        pages.push({ items: currentPage, pageNum: pageNumber++ })
        currentPage = []
        currentHeight = 0
      }

      currentPage.push({
        type: 'category',
        code,
        name,
        comment: catData.comment,
        rating: catData.rating,
        photos: catPhotos,
        sectionLetter // Track which section this category belongs to
      })
      currentHeight += itemHeight
    })

    // Add custom categories for this section
    getCustomCategoriesForSection(sectionLetter).forEach((customCat) => {
      const catPhotos = getPhotosForCategory(customCat.code)
      const catData = categoryData[customCat.code] || {}
      const hasContent = catData.comment || catPhotos.length > 0

      const itemHeight = hasContent
        ? estimateCategoryHeight(catData.comment, catPhotos.length)
        : EMPTY_CATEGORY_HEIGHT

      if (currentHeight + itemHeight > CONTENT_HEIGHT) {
        pages.push({ items: currentPage, pageNum: pageNumber++ })
        currentPage = []
        currentHeight = 0
      }

      currentPage.push({
        type: 'category',
        code: customCat.code,
        name: customCat.name,
        comment: catData.comment,
        rating: catData.rating,
        photos: catPhotos,
        sectionLetter,
        isCustom: true
      })
      currentHeight += itemHeight
    })
  })

  // Add standalone custom categories section if any exist
  if (standaloneCategories.length > 0) {
    // Start new page for standalone categories
    if (currentPage.length > 0) {
      pages.push({ items: currentPage, pageNum: pageNumber++ })
      currentPage = []
      currentHeight = 0
    }

    // Add a header for custom categories section
    currentPage.push({
      type: 'customSectionHeader',
      surveyLevel
    })
    currentHeight += 80

    // Add each standalone category
    standaloneCategories.forEach((customCat) => {
      const catPhotos = getPhotosForCategory(customCat.code)
      const catData = categoryData[customCat.code] || {}
      const hasContent = catData.comment || catPhotos.length > 0

      const itemHeight = hasContent
        ? estimateCategoryHeight(catData.comment, catPhotos.length)
        : EMPTY_CATEGORY_HEIGHT

      if (currentHeight + itemHeight > CONTENT_HEIGHT) {
        pages.push({ items: currentPage, pageNum: pageNumber++ })
        currentPage = []
        currentHeight = 0
      }

      currentPage.push({
        type: 'category',
        code: customCat.code,
        name: customCat.name,
        comment: catData.comment,
        rating: catData.rating,
        photos: catPhotos,
        sectionLetter: 'X',
        isCustom: true
      })
      currentHeight += itemHeight
    })
  }

  // Don't forget the last page
  if (currentPage.length > 0) {
    pages.push({ items: currentPage, pageNum: pageNumber })
  }

  const totalPages = pages.length

  return (
    <div className="pdf-preview" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {pages.map((page) => (
        <A4Page key={page.pageNum} pageNumber={page.pageNum} totalPages={totalPages} surveyLevel={surveyLevel}>
          {page.items.map((item, idx) => {
            if (item.type === 'header') {
              return (
                <SectionHeader
                  key={`header-${item.sectionLetter}`}
                  sectionLetter={item.sectionLetter}
                  surveyLevel={item.surveyLevel}
                  limitationsText={item.limitationsText}
                  editMode={editMode}
                  onLimitationsChange={(value) => onCategoryChange(item.limitationsKey, 'comment', value)}
                />
              )
            }
            if (item.type === 'customSectionHeader') {
              return (
                <div key="custom-header" style={{ marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e3a5f', marginBottom: '15px' }}>
                    Additional Categories
                  </h2>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px', alignItems: 'center' }}>
                    <RatingCircle rating="1" size={20} />
                    <RatingCircle rating="2" size={20} />
                    <RatingCircle rating="3" size={20} />
                    <RatingCircle rating="NI" size={20} />
                  </div>
                </div>
              )
            }
            return (
              <CategoryBlock
                key={item.code}
                code={item.code}
                name={item.name}
                comment={item.comment}
                rating={item.rating}
                photos={item.photos}
                editMode={editMode}
                onCommentChange={(value) => onCategoryChange(item.code, 'comment', value)}
                onRatingChange={(value) => onCategoryChange(item.code, 'rating', value)}
                usesPngHeader={!!SECTION_HEADERS[item.sectionLetter]}
              />
            )
          })}
        </A4Page>
      ))}
    </div>
  )
}

export default function ReviewSurveyPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isMobile = useMobileLayout()
  const isPreviewOnly = searchParams.get('preview') === 'true'
  const previewRef = useRef(null)

  const [categoryData, setCategoryData] = useState({})
  const [saving, setSaving] = useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [editMode, setEditMode] = useState(true)
  const [zoom, setZoom] = useState(1.5) // 1.5 actual = 100% displayed
  const [generatingPdf, setGeneratingPdf] = useState(false)
  const initializedForSurveyRef = useRef(null)

  const currentSurvey = useSurveyStore(state => state.currentSurvey)
  const currentPhotos = useSurveyStore(state => state.currentPhotos)
  const currentAiResponses = useSurveyStore(state => state.currentAiResponses)
  const loading = useSurveyStore(state => state.loading)
  const loadSurvey = useSurveyStore(state => state.loadSurvey)
  const updateSurvey = useSurveyStore(state => state.updateSurvey)
  const clearCurrentSurvey = useSurveyStore(state => state.clearCurrentSurvey)
  const customCategories = useCategoryStore(state => state.customCategories)
  const loadCategories = useCategoryStore(state => state.loadCategories)

  // Load survey data and categories
  useEffect(() => {
    if (id) {
      loadSurvey(id)
      loadCategories()
    }
    return () => clearCurrentSurvey()
  }, [id, loadSurvey, loadCategories, clearCurrentSurvey])

  // Initialize category data from AI responses - only once per survey
  useEffect(() => {
    if (!currentSurvey) return
    if (initializedForSurveyRef.current === currentSurvey.id) return

    console.log('Initializing categoryData from survey:', currentSurvey.id)

    initializedForSurveyRef.current = currentSurvey.id
    const initialData = {}

    // Get most recent AI response for each category
    const sortedResponses = [...(currentAiResponses || [])]
      .filter(r => r.success)
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

    for (const response of sortedResponses) {
      if (!initialData[response.categoryCode]) {
        initialData[response.categoryCode] = {
          comment: response.response || '',
          rating: null
        }
      }
    }

    // Load saved review data if exists (overrides AI responses)
    if (currentSurvey.reviewData) {
      for (const [code, data] of Object.entries(currentSurvey.reviewData)) {
        initialData[code] = {
          comment: data.comment ?? initialData[code]?.comment ?? '',
          rating: data.rating ?? null
        }
      }
    }

    setCategoryData(initialData)
  }, [currentSurvey, currentAiResponses])

  // Handle category data change
  const handleCategoryChange = useCallback((code, field, value) => {
    setCategoryData(prev => ({
      ...prev,
      [code]: {
        ...prev[code],
        [field]: value
      }
    }))
    setHasUnsavedChanges(true)
  }, [])

  // Save review data
  const handleSave = async () => {
    if (!currentSurvey) return
    setSaving(true)
    try {
      await updateSurvey(currentSurvey.id, {
        reviewData: categoryData
      })
      setHasUnsavedChanges(false)
    } catch (error) {
      console.error('Failed to save:', error)
      alert('Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  // Handle preview button - on iOS, generate PDF and open in new tab
  const handlePreviewToggle = async () => {
    if (editMode && isIOS()) {
      // Open window immediately (before async) to avoid popup blocker
      const pdfWindow = window.open('about:blank', '_blank')
      if (pdfWindow) {
        pdfWindow.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui"><p>Generating PDF...</p></body></html>')
      }

      setGeneratingPdf(true)
      try {
        const blob = await pdf(
          <SurveyDocument
            survey={currentSurvey}
            photos={currentPhotos}
            categoryData={categoryData}
            customCategories={customCategories}
          />
        ).toBlob()
        const url = URL.createObjectURL(blob)
        if (pdfWindow) {
          pdfWindow.location.href = url
        }
        // Don't revoke immediately - let the window load it
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (error) {
        console.error('Failed to generate PDF:', error)
        if (pdfWindow) {
          pdfWindow.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:system-ui;color:red"><p>Error: ' + error.message + '</p></body></html>')
        }
        alert('Failed to generate PDF. Please try again.')
      } finally {
        setGeneratingPdf(false)
      }
    } else {
      setEditMode(!editMode)
    }
  }

  if (!currentSurvey && !loading) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Survey not found</h2>
        <Button variant="primary" onClick={() => navigate('/surveys')}>
          View All Surveys
        </Button>
      </div>
    )
  }

  if (loading || !currentSurvey) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  // Mobile preview-only mode - generate PDF and provide share/open options
  if (isMobile && isPreviewOnly) {
    const pdfFilename = `${currentSurvey.propertyName || 'Survey'}_Report.pdf`

    return (
      <div className="flex flex-col h-full -m-4">
        {/* Header */}
        <div className="bg-blue-600 text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => navigate(`/survey/${id}`)}
            className="p-2 -ml-2 hover:bg-blue-500 rounded-lg"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate">PDF Preview</h1>
            <p className="text-sm text-blue-200 truncate">{currentSurvey.propertyName}</p>
          </div>
        </div>

        {/* PDF Generation */}
        <div className="flex-1 overflow-hidden bg-slate-100 flex flex-col">
          <BlobProvider document={
            <SurveyDocument
              survey={currentSurvey}
              photos={currentPhotos}
              categoryData={categoryData}
              customCategories={customCategories}
            />
          }>
            {({ blob, url, loading: pdfLoading, error }) => {
              // Share handler - uses Web Share API on iOS
              const handleShare = async () => {
                if (!blob) return
                try {
                  const file = new File([blob], pdfFilename, { type: 'application/pdf' })
                  if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                    await navigator.share({ files: [file], title: pdfFilename })
                  } else {
                    // Fallback - open in new tab
                    window.open(url, '_blank')
                  }
                } catch (err) {
                  console.log('Share cancelled or failed:', err)
                }
              }

              // Open in viewer - opens blob URL directly
              const handleOpen = () => {
                if (url) {
                  window.open(url, '_blank')
                }
              }

              if (pdfLoading) {
                return (
                  <div className="flex flex-col items-center justify-center h-full p-4">
                    <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4" />
                    <p className="text-slate-600 text-lg">Generating PDF...</p>
                  </div>
                )
              }
              if (error) {
                return (
                  <div className="flex flex-col items-center justify-center h-full p-4">
                    <p className="text-red-500 text-lg mb-4">Error generating PDF</p>
                    <button
                      onClick={() => navigate(`/survey/${id}`)}
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold"
                    >
                      Go Back
                    </button>
                  </div>
                )
              }

              // PDF ready - show options (iOS Safari doesn't support iframe PDFs)
              return (
                <div className="flex flex-col items-center justify-center h-full p-6">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
                    <Eye className="w-10 h-10 text-emerald-600" />
                  </div>
                  <p className="text-slate-900 text-xl font-semibold mb-2">PDF Ready</p>
                  <p className="text-slate-500 text-sm mb-8 text-center">
                    {currentSurvey.propertyName}
                  </p>

                  <div className="space-y-3 w-full max-w-xs">
                    <button
                      onClick={handleShare}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-purple-600 text-white rounded-xl font-semibold text-lg active:bg-purple-700"
                    >
                      <ExternalLink className="w-5 h-5" />
                      Share PDF
                    </button>
                    <button
                      onClick={handleOpen}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg active:bg-blue-700"
                    >
                      <Eye className="w-5 h-5" />
                      Open in Viewer
                    </button>
                    <button
                      onClick={() => navigate(`/survey/${id}`)}
                      className="w-full px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-semibold active:bg-slate-300"
                    >
                      Back to Survey
                    </button>
                  </div>
                </div>
              )
            }}
          </BlobProvider>
        </div>
      </div>
    )
  }

  const iosDevice = isIOS()

  return (
    <div className="absolute inset-0 flex flex-col bg-slate-100">
      {/* Fixed toolbar - single row with ratings and controls */}
      <div className="flex-shrink-0 bg-white shadow-sm border-b border-slate-200 no-print z-10">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between gap-4">
            {/* Ratings guide - left side */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <RatingCircle rating="1" size={18} />
                <span className="text-slate-600 hidden sm:inline">No repair</span>
              </div>
              <div className="flex items-center gap-1.5">
                <RatingCircle rating="2" size={18} />
                <span className="text-slate-600 hidden sm:inline">Minor</span>
              </div>
              <div className="flex items-center gap-1.5">
                <RatingCircle rating="3" size={18} />
                <span className="text-slate-600 hidden sm:inline">Serious</span>
              </div>
              <div className="flex items-center gap-1.5">
                <RatingCircle rating="NI" size={18} />
                <span className="text-slate-600 hidden sm:inline">Not inspected</span>
              </div>
            </div>

            {/* Controls - right side */}
            <div className="flex items-center gap-2">
              {/* Zoom controls */}
              <div className="flex items-center gap-1 border-r pr-2 mr-2">
                <button
                  onClick={() => setZoom(z => Math.max(0.45, z - 0.15))}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-600 w-10 text-center">{Math.round((zoom / 1.5) * 100)}%</span>
                <button
                  onClick={() => setZoom(z => Math.min(2.25, z + 0.15))}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
              </div>

              {/* Edit/Preview toggle */}
              <button
                onClick={handlePreviewToggle}
                disabled={generatingPdf}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm font-medium ${
                  editMode
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-slate-100 text-slate-700'
                } ${generatingPdf ? 'opacity-50' : ''}`}
              >
                {generatingPdf ? (
                  <div className="spinner w-4 h-4" />
                ) : editMode ? (
                  <Eye className="w-4 h-4" />
                ) : (
                  <Edit3 className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{generatingPdf ? 'Opening...' : editMode ? 'Preview' : 'Edit'}</span>
              </button>

              {hasUnsavedChanges && (
                <span className="text-xs text-amber-600">Unsaved</span>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleSave}
                loading={saving}
                disabled={!hasUnsavedChanges || saving}
              >
                <Save className="w-4 h-4 mr-1" />
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview area - scrollable content */}
      <div className="flex-1 overflow-y-auto py-8">
        {editMode ? (
          /* Edit Mode - HTML editor */
          <div
            ref={previewRef}
            style={{
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            }}
          >
            <PDFPreview
              photos={currentPhotos}
              categoryData={categoryData}
              onCategoryChange={handleCategoryChange}
              editMode={editMode}
              surveyLevel={currentSurvey.surveyLevel || 1}
              customCategories={customCategories}
            />
          </div>
        ) : (
          /* Preview Mode - Real PDF Viewer */
          <div style={{ height: '100%', display: 'flex', justifyContent: 'center', padding: '0 20px' }}>
            {isIOS() ? (
              /* iOS uses BlobProvider + iframe for better compatibility */
              <BlobProvider document={
                <SurveyDocument
                  survey={currentSurvey}
                  photos={currentPhotos}
                  categoryData={categoryData}
                  customCategories={customCategories}
                />
              }>
                {({ blob, url, loading, error }) => {
                  const handleShare = async () => {
                    const filename = `${currentSurvey.propertyName || 'Survey'}_Report.pdf`
                    // Try Web Share API first (works great on iOS)
                    if (navigator.share && navigator.canShare) {
                      try {
                        const file = new File([blob], filename, { type: 'application/pdf' })
                        if (navigator.canShare({ files: [file] })) {
                          await navigator.share({ files: [file], title: filename })
                          return
                        }
                      } catch (err) {
                        console.log('Share failed, falling back to download:', err)
                      }
                    }
                    // Fallback: download link
                    const downloadUrl = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = downloadUrl
                    a.download = filename
                    document.body.appendChild(a)
                    a.click()
                    document.body.removeChild(a)
                    URL.revokeObjectURL(downloadUrl)
                  }

                  if (loading) {
                    return <div className="flex items-center justify-center h-full"><div className="spinner w-8 h-8" /></div>
                  }
                  if (error) {
                    return <div className="text-red-500">Error generating PDF: {error.message}</div>
                  }
                  return (
                    <div style={{ width: '100%', maxWidth: '1200px', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'center' }}>
                        <button
                          onClick={handleShare}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Share / Save PDF
                        </button>
                      </div>
                      <iframe
                        src={url}
                        style={{ width: '100%', flex: 1, border: 'none' }}
                        title="PDF Preview"
                      />
                    </div>
                  )
                }}
              </BlobProvider>
            ) : (
              /* Desktop uses PDFViewer */
              <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="spinner w-8 h-8" /></div>}>
                <PDFViewer
                  width="100%"
                  height="100%"
                  style={{ maxWidth: '1200px', border: 'none' }}
                  showToolbar={true}
                >
                  <SurveyDocument
                    survey={currentSurvey}
                    photos={currentPhotos}
                    categoryData={categoryData}
                    customCategories={customCategories}
                  />
                </PDFViewer>
              </Suspense>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
